import { MAX_QUOTES, savedEvidenceOf, quoteCount, evidenceToSend } from './evidencePayload';
import { evidenceKey } from './citeQuote';
import { evidenceChosenByReviewer } from './assessment';

/**
 * One question's evidence while Override is open (develop's inline editor, routes/Dashboard). The evidence part
 * of the hl line's correctionDraft.js, on its own: develop keeps its own answer/score/reason editor.
 *
 * Unlike hl, the saved quotes start KEPT: a reviewer removes what they disagree with and adds lines of their
 * own. A question whose evidence is left as it opened sends none, and the server leaves it as it is; one whose
 * evidence changed sends its whole final list — `[]` when everything was removed, which clears it.
 */

/** Every saved quote starts kept, except a second copy of one (the server would refuse it twice) and any past
 * the limit — so the defaults are always a list the server accepts. */
export function defaultKeepOf(saved) {
  const seen = new Set();
  let kept = 0;
  return saved.map((row) => {
    const key = evidenceKey(row);
    if (seen.has(key) || kept >= MAX_QUOTES) return false;
    seen.add(key);
    kept += 1;
    return true;
  });
}

export function draftEvidenceOf(parsed) {
  const saved = savedEvidenceOf(parsed);
  const keep = defaultKeepOf(saved);
  // `human`: the evidence was kept or cited by a reviewer in an earlier correction (the editor says so).
  return { saved, keep, defaultKeep: keep, cited: [], human: evidenceChosenByReviewer(parsed) };
}

/** The evidenceKey()s kept or cited on a question now (the picker marks those lines Cited). */
export const takenKeysOf = (d) => (d ? [...d.saved.filter((_, i) => d.keep[i]), ...d.cited].map(evidenceKey) : []);
const keysOf = takenKeysOf;

/** Is the same quote (evidenceKey) already kept or cited on this question? The server would refuse it twice. */
export const alreadyOnQuestion = (d, row) => Boolean(d && row) && keysOf(d).includes(evidenceKey(row));

export function evidenceReducer(d, action) {
  if (!d) return d;
  if (action.type === 'keep') {
    if (action.value && !d.keep[action.index]) {
      if (quoteCount(d.keep, d.cited) >= MAX_QUOTES || alreadyOnQuestion(d, d.saved[action.index])) return d;
    }
    return { ...d, keep: d.keep.map((k, i) => (i === action.index ? Boolean(action.value) : k)) };
  }
  if (action.type === 'cite') {
    if (quoteCount(d.keep, d.cited) >= MAX_QUOTES || alreadyOnQuestion(d, action.citation)) return d;
    return { ...d, cited: [...d.cited, action.citation] };
  }
  if (action.type === 'uncite') return { ...d, cited: d.cited.filter((_, i) => i !== action.index) };
  return d;
}

export const evidenceChanged = (d) =>
  Boolean(d) && (d.keep.some((k, i) => k !== d.defaultKeep[i]) || d.cited.length > 0);

/** The list to send for a changed question: what is kept and cited, or [] when the reviewer removed it all. */
export const evidencePayloadOf = (d) => evidenceToSend(d.saved, d.keep, d.cited) || [];
